import React, { useEffect, useState, useRef } from 'react';
import { injectIntl } from 'react-intl';
import { Stage, Layer, Group, Rect, Text, Line } from 'react-konva';
import { setupStageTypography, i18nIntl, loadKonvaImage, getPromotionTextColor, getPromotionMainColor, getLayout, getSoundPropsById } from '../../lib/ep.js';
import { Howl } from 'howler';
//import "gifler";
import EditableArea from '../canvas/EditableArea';
import { Particles } from './Particles.js';

const standard_board_size = 600;	// px
const img_path = `${window.cdn_url}img/games_gui/scratch/`;
const default_area_coords = {
	x:0.25,
	y:0.25,
	w:0.5,
	h:0.5,
};

const alpha_threshold = 10;	// range: 0-255
const uncovered_area_to_prize = 90;	// range: 0-100
const data_in_each_pixel = 4; // r, g, b, a

const guides_pos = [0.25, 0.5, 0.75];
const guides_color = "#FF0000";
const guides_bg_props = {
	stroke:'#FFFFFF',
	strokeWidth:3,
};

const use_scratch_sound = (window.server_type != '0' || true) ? true : false;

const ScratchCanvas = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const scratch_props = (props.scratch != undefined) ? props.scratch : {};
	let code = (scratch_props.title != undefined && scratch_props.title != "") ? scratch_props.title : props?.prizeType?.title;
	if(props.scratch == undefined || props.scratch.id === 0) {
		if(code == undefined || code == "") code = i18n("No prize");
	}

	const promotion = props.promotion;
	const bg_color = scratch_props?.bg_color ?? getPromotionMainColor(promotion);
	const text_color = scratch_props?.text_color ?? getPromotionTextColor(promotion);
	const font_size = scratch_props?.font_size ?? 3;
	const display_textual_part = (scratch_props?.show_prize_name && (scratch_props?.show_prize_name=='true' || scratch_props?.show_prize_name===true))  ?? true;
	const display_promo_code = scratch_props?.show_promo_code ?? false;
	const default_cache_props = { pixelRatio:2, imageSmoothingEnabled:true, offset:5, drawBorder:false };
	const layout_props = getLayout(promotion);
	const { typography, typography_weight } = setupStageTypography((props?.stage?.font_family ?? 'main'), layout_props.text);

	const [ size, setSize ] = useState(standard_board_size);
	const scale = useRef(1);

	let lastPoint;

	const silence = props?.stage?.silence ?? false;

	const is_builder = (props.is_builder != undefined) ? props.is_builder : false;
	const is_editing = (is_builder && props.is_editing != undefined) ? props.is_editing : false;
	const is_ghosting = (is_editing && props.is_ghosting != undefined) ? props.is_ghosting : false;
	const show_grid = (is_editing && props.show_grid != undefined) ? props.show_grid : false;

	const sounds = useRef({});

	useEffect(() => {
		if(silence) return;

		sounds.current.sound_prize = new Howl({ ...getSoundPropsById('applause') });
		sounds.current.sound_success = new Howl({ ...getSoundPropsById('success') });
		sounds.current.sound_no_prize = new Howl({ ...getSoundPropsById('oh') });
		sounds.current.sound_scratch =new Howl({ ...getSoundPropsById('scratch', { loop:true }) });
	}, []);

	const soundOK = () => {
		if(silence) return;
		sounds.current.sound_prize.play();
	};
	const soundSuccess = () => {
		if(silence) return;
		sounds.current.sound_success.play();
	};
	const soundKO = () => {
		if(silence) return;
		sounds.current.sound_no_prize.play();
	};

	const isDrawing = useRef(false);
	const isScratching = useRef(false);
	const brush_image = useRef(false);
	const code_image = useRef(false);
	let refs = useRef([]);
	
	const getAbsoluteCoords = (coords_rel, adapt_retina) => {
		if(adapt_retina == undefined) adapt_retina = false;
		if(refs.current['paint_layer_ref'] == undefined) return coords_rel;

		let multiplier = (adapt_retina) ? refs.current['paint_layer_ref'].getCanvas().pixelRatio : 1;
		return {
			w:coords_rel.w * size * multiplier,
			h:coords_rel.h * size * multiplier,
			x:coords_rel.x * size * multiplier,
			y:coords_rel.y * size * multiplier,
		};
	};

	const [cover_image_obj, setCoverImageObj] = useState({});
	const [fully_loaded, setFullyLoaded] = useState(false);
	const [code_image_obj, setCodeImageObj] = useState({});
	const [is_game_finished, setIsGameFinished] = useState(false);
	const [area, setArea] = useState((scratch_props.area != undefined) ? scratch_props.area : default_area_coords);
	const [area_abs, setAreaAbs] = useState(getAbsoluteCoords(area));

	useEffect(() => {
		if(refs.current['paint_layer_ref'] == undefined) return;
		setAreaAbs(getAbsoluteCoords(area))
	}, [area, refs.current['paint_layer_ref'], size]);
	
	const getCanvasContext = () => {
		if(refs.current['paint_layer_ref'] == undefined) return false;
		return refs.current['paint_layer_ref'].getCanvas().getContext();
	};

	const getFilledInPixels = (increment) => {
		if(!increment || increment < 1) increment = 1;
		if(refs.current['paint_layer_ref'] == undefined) return 0;

		const ctx = getCanvasContext();
		if(!ctx) return false;

		const area_absolute = getAbsoluteCoords(area, true);
		const pixels = ctx.getImageData(area_absolute.x, area_absolute.y, area_absolute.w, area_absolute.h);
		const pixel_data = pixels.data;
		const pixel_length = pixel_data.length;
		const total = pixel_length / increment;
		let count = 0;

		for(let i = 0; i < pixel_length; i += increment) {
			// we only take in account the 4th value of each pixel (the alpha value)
			// to avoid issues with black cover images:
			if(parseInt(pixel_data[i + 3]) < alpha_threshold) count++;
		}

		return Math.round((count / total) * 100);
	};

	const distanceBetween = (point1, point2) => {
		if(point1 == undefined || point2 == undefined) return 0;
		return Math.sqrt(Math.pow(point2.x - point1.x, 2) + Math.pow(point2.y - point1.y, 2));
	};

	const angleBetween = (point1, point2) => {
		if(point1 == undefined || point2 == undefined) return 0;
		return Math.atan2(point2.x - point1.x, point2.y - point1.y);
	};

	const handlePercentage = (filledInPixels) => {
		filledInPixels = filledInPixels || 0;
		if(filledInPixels > uncovered_area_to_prize) {
			setIsGameFinished(true);
			stopScratchSound();
			if(props.onFinish != undefined) props.onFinish();
			setTimeout(() => {
				if(props.prizeType.id != 0) {
					soundOK();
					soundSuccess();
				} else {
					soundKO();
				}
			}, 50);
		}
	};

	const handleMouseDown = (e) => {
		if(is_game_finished || is_editing) return;
		isDrawing.current = true;
		const stage = e.target.getStage();
		lastPoint = stage.getPointerPosition();
	};

	const loadCoverImage = () => {
		const img_url = (props.stage?.scratch_cover != undefined) ? props.stage.full_scratch_cover : `${img_path}tapper.jpg`;
		let img = loadKonvaImage(img_url, () => {
			setCoverImageObj((old) => {
				return {
					fillPatternImage:img,
					image_w:img.width,
					image_h:img.height,
				}
			});
		});
	};

	const loadBrushImage = () => {
		const img_url = (props.brush_image != undefined) ? props.brush_image : `${img_path}brush.png`;
		let img = loadKonvaImage(img_url, () => {
			brush_image.current = img;
		});
	};

	const loadCodeImage = () => {
		if(!props?.scratch?.scratch_image) {
			setCodeImageObj({});
			code_image.current = false;
			generateCache();
			return;
		}

		let img = loadKonvaImage(props.scratch.full_scratch_image, () => {
			setCodeImageObj((old) => {
				generateCache();

				code_image.current = img;
				return {
					fillPatternImage:img,
					image_w:img.width,
					image_h:img.height,
				}
			});
		});
	};

	const handleMouseMove = (e) => {
		if(!isDrawing.current || is_game_finished || is_editing) return;

		playScratchSound();

		const stage = e.target.getStage();
		const currentPoint = stage.getPointerPosition();
		const dist = distanceBetween(lastPoint, currentPoint);
		const angle = angleBetween(lastPoint, currentPoint);
		let x, y;

		const ctx = getCanvasContext();
		if(!ctx) return false;

		for(var i = 0; i < dist; i++) {
			x = lastPoint.x + (Math.sin(angle) * i) - 25;
			y = lastPoint.y + (Math.cos(angle) * i) - 25;
			ctx.globalCompositeOperation = 'destination-out';
			ctx.drawImage(brush_image.current, x, y);
		}

		lastPoint = currentPoint;
		handlePercentage(getFilledInPixels(8 * data_in_each_pixel));
	};

	const playScratchSound = () => {
		if(silence) return;

		if(use_scratch_sound && !isScratching.current) {
			sounds.current.sound_scratch.play();
			isScratching.current = true;
		}
	};

	const stopScratchSound = () => {
		if(silence) return;

		if(use_scratch_sound) {
			isScratching.current = false;
			sounds.current.sound_scratch.stop();
		}
	};

	const handleMouseUp = (e) => {
		isDrawing.current = false;
		
		stopScratchSound();

		const ctx = getCanvasContext();
		if(!ctx) return false;
		
		ctx.globalCompositeOperation = 'source-over';
	};

	const loadKonvaImages = (is_first_load) => {
		if(is_first_load == undefined) is_first_load = false;
		loadCoverImage();
		loadCodeImage();
		if(is_first_load) loadBrushImage();
	};

	const getImageSize = (img) => {
		return (size + 1) / (Math.max(img.image_w, img.image_h));
	};

	const onChangeArea = (new_area) => {
		if(!props.is_editing) return;
		if(props.updateArea != undefined) props.updateArea(new_area);
		setArea(new_area);
	};
	
	const clearCache = () => {
		if(!is_builder && refs.current['prize_won'] != undefined) {
			refs.current['prize_won'].clearCache();
		}
	};

	const generateCache = () => {
		if(!is_builder && refs.current['prize_won'] != undefined) {
			clearCache();
			setTimeout(() => {
				refs.current['prize_won'].cache(default_cache_props);
			}, 100);
		}
	};

	useEffect(() => {
		if(props.canvas_size != undefined) {
			loadKonvaImages(true);
			setSize(props.canvas_size);
			setFullyLoaded(true);
			scale.current = props.canvas_size / standard_board_size;
		}
	}, [props.canvas_size]);

	useEffect(() => {
		if(props.scratch != undefined) {
			setArea((props.scratch.area != undefined) ? props.scratch.area : default_area_coords);
			generateCache();
		}
	}, [props.scratch]);

	useEffect(() => {
		generateCache();
	}, [typography, typography_weight]);

	useEffect(() => {
		loadKonvaImages(false);
		if(is_builder) {
			setIsGameFinished(false);
		}
	}, [props.prizeType, props.scratch]);

	useEffect(() => {
		if(is_builder) {
			loadCoverImage();
			setIsGameFinished(false);
		}
	}, [props.stage.scratch_cover,props.is_editing]);

	const cover_size = getImageSize(cover_image_obj);
	const code_size = (code_image_obj.fillPatternImage != undefined) ? getImageSize(code_image_obj) : false;

	if(!fully_loaded || cover_image_obj.fillPatternImage == undefined) return null;

	let stage_classes = ['canvas_cont'];

	const getPromoCode = () => {
		if(props.prizeType.type == 2) return props.prizeType.unique_code;
		if(props.prizeType.type == 1 || props.prizeType.redeemable) return props?.userInfo?.c;
	};

	let textual_part = "";
	if(display_textual_part) textual_part += code;
	if(display_textual_part && display_promo_code ) {
		if(textual_part != "") textual_part += "\n";
		textual_part += getPromoCode();
	}
	const textual_part_size = size * 0.94;

	return (
		<Stage
			ref={(node) => { refs.current[`main_stage`] = node; }}
			className={stage_classes.join(" ")}
			width={size}
			height={size}
			onMouseDown={handleMouseDown}
			onTouchStart={handleMouseDown}
			onMousemove={handleMouseMove}
			onTouchMove={handleMouseMove}
			onMouseup={handleMouseUp}
			onTouchEnd={handleMouseUp}
		>
			<Layer ref={(node) => { refs.current['prize_won'] = node; }}>
				<Rect width={size} height={size} fill={bg_color} />

				{(code_size !== false) && (
					<Group ref={(node) => { refs.current['prize_image'] = node; }}>
						<Rect
							width={size}
							height={size}
							fillPatternImage={code_image_obj.fillPatternImage}
							fillPatternRepeat='no-repeat'
							fillPatternScaleX={code_size}
							fillPatternScaleY={code_size}
							fillPatternOffsetX={code_size / 2}
							fillPatternOffsetY={code_size / 2}
						/>
					</Group>
				)}

				{(display_textual_part && textual_part != "") && (
					<Group
						x={(area_abs.x) - (textual_part_size - area_abs.w) / 2}
						y={(area_abs.y) - (textual_part_size - area_abs.h) / 2}
					>
						<Text
							width={textual_part_size}
							height={textual_part_size}
							fontSize={(50 - ((3 - font_size) * 10)) * scale.current}
							fontFamily={typography}
							fontStyle={typography_weight}
							fill={text_color}
							align='center'
							verticalAlign='middle'
							text={textual_part}
							lineHeight={1.2}
						/>
					</Group>
				)}
			</Layer>

			{(!is_game_finished) && (
				<Layer ref={(node) => { refs.current['paint_layer_ref'] = node; }}>
					<Rect
						width={size}
						height={size}
						fillPatternImage={cover_image_obj.fillPatternImage}
						fillPatternRepeat='no-repeat'
						fillPatternScaleX={cover_size}
						fillPatternScaleY={cover_size}
						fillPatternOffsetX={cover_size / 2}
						fillPatternOffsetY={cover_size / 2}
						opacity={(is_ghosting) ? 0.4 : ((!is_editing) ? 1 : 0)}
					/>

					{show_grid && guides_pos.map((g) => {
						const points_h = [0, size * g, size, size * g];
						const points_v = [size * g, 0, size * g, size];
						const dash_unit = (g == 0.5) ? 7 : 3;
						const guides_props = {
							dash:[dash_unit, dash_unit],
							stroke:'#000000',
							strokeWidth:(g == 0.5) ? 1 : 0.5,
						};

						return (
							<React.Fragment key={`guides_${g}`}>
								<Line points={points_h} {...guides_bg_props} />
								<Line points={points_v} {...guides_bg_props} />

								<Line points={points_h} {...guides_props} />
								<Line points={points_v} {...guides_props} />
							</React.Fragment>
						)
					})}

					{(is_builder) && (
						<EditableArea
							area={area}
							areaChanged={onChangeArea}
							areaMove={setArea}
							guides_color={guides_color}
							is_editing={is_editing}
							size={size}
						/>
					)}
				</Layer>
			)}

			{(!is_game_finished && !is_editing) && (
				<Particles
					stage={refs.current[`main_stage`]}
					canvas_context={getCanvasContext()}
					params={{
						Gravity:0.2,
					}
				}/>
			)}
		</Stage>
	)
};

export default injectIntl(ScratchCanvas);